import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Document, Page, Text, View, StyleSheet, pdf, Image } from '@react-pdf/renderer';
import img from '../images-src/logo.jpeg';

const ListReceipts = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewReceipt, setPreviewReceipt] = useState(null);
  
  // Search and filter states
  const [searchReceiptNo, setSearchReceiptNo] = useState('');
  const [searchCustomer, setSearchCustomer] = useState('');
  const [searchInvoiceNo, setSearchInvoiceNo] = useState('');
  const [searchPaymentMode, setSearchPaymentMode] = useState('');

  const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Fetch receipts on component mount
  useEffect(() => {
    fetchReceipts();
  }, []);

  const fetchReceipts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiUrl}/api/receipts-list`);
      setReceipts(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch (err) {
      console.error('Error fetching receipts:', err);
      setError('Failed to load receipts');
      setReceipts([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter receipts based on search criteria
  const filteredReceipts = receipts.filter(receipt => {
    const receiptNoMatch = receipt.receipt_number
      ?.toLowerCase()
      .includes(searchReceiptNo.toLowerCase());
    const customerMatch = receipt.invoice?.customer?.name
      ?.toLowerCase()
      .includes(searchCustomer.toLowerCase());
    const invoiceNoMatch = receipt.invoice?.invoice_no
      ?.toLowerCase()
      .includes(searchInvoiceNo.toLowerCase());
    const paymentModeMatch = searchPaymentMode === '' || receipt.payment_mode === searchPaymentMode;

    return receiptNoMatch && customerMatch && invoiceNoMatch && paymentModeMatch;
  });

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Handle delete receipt
  const handleDelete = async (receiptId) => {
    if (window.confirm('Are you sure you want to delete this receipt?')) {
      try {
        await axios.delete(`${apiUrl}/api/receipts/${receiptId}`);
        setReceipts(receipts.filter(receipt => receipt.id !== receiptId));
      } catch (err) {
        console.error('Error deleting receipt:', err);
        alert('Failed to delete receipt. Please try again.');
      }
    }
  };

  // PDF Document Component
  const ReceiptPDFDocument = ({ receipt }) => {
    const customerName = receipt.invoice?.customer?.name || 'Unknown';
    const invoiceNo = receipt.invoice?.invoice_no || 'N/A';
    const invoiceTotal = receipt.ilines_sum_line_total || receipt.invoice?.ilines_sum_line_total || 0;
    const paymentMode = receipt.payment_mode || '';
    
    // Calculate balance - get all receipts for this invoice and sum them
    const allReceiptsForInvoice = receipts.filter(r => r.invoice_id === receipt.invoice_id || r.invoice?.id === receipt.invoice_id);
    const totalPaidBefore = allReceiptsForInvoice.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
    const balance = parseFloat(invoiceTotal) - totalPaidBefore;
    
    const receiptStyles = StyleSheet.create({
      page: {
        padding: 10,
        fontSize: 8,
        fontFamily: 'Helvetica',
      },
      header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 10,
        borderBottomWidth: 2,
        borderBottomColor: '#0891b2',
        paddingBottom: 20,
      },
      headerLeft: {
        flex: 1,
      },
      headerRight: {
        flex: 1,
      },
      receiptTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#0891b2',
        textAlign: 'right',
        marginBottom: 5,
      },
      companyName: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#000',
        textAlign: 'right',
        marginBottom: 2,
      },
      companyDetails: {
        fontSize: 8,
        color: '#333',
        textAlign: 'right',
        marginBottom: 1,
      },
      infoSection: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 15,
        backgroundColor: '#ffffff',
        padding: 10,
      },
      infoBlock: {
        flex: 1,
        marginRight: 15,
      },
      infoLabel: {
        fontSize: 8,
        color: '#333',
        marginBottom: 2,
        textTransform: 'uppercase',
        fontWeight: 'bold',
      },
      infoValue: {
        fontSize: 10,
        fontWeight: '600',
        color: '#000',
      },
      contentContainer: {
        flexDirection: 'row',
        gap: 20,
        marginBottom: 15,
      },
      leftColumn: {
        flex: 1,
      },
      rightColumn: {
        flex: 1,
      },
      sectionBox: {
        marginBottom: 12,
        borderWidth: 1,
        borderColor: '#d4af37',
        padding: 8,
        backgroundColor: '#fff',
      },
      sectionLabel: {
        fontSize: 8,
        fontWeight: 'bold',
        color: '#555',
        marginBottom: 4,
        textTransform: 'uppercase',
      },
      sectionValue: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#000',
      },
      row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 6,
        paddingBottom: 4,
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
      },
      label: {
        fontSize: 8,
        color: '#666',
        fontWeight: 'bold',
      },
      value: {
        fontSize: 9,
        color: '#000',
        fontWeight: 'bold',
        textAlign: 'right',
      },
      allocationsTable: {
        marginTop: 15,
        marginBottom: 15,
        borderWidth: 1,
        borderColor: '#d4af37',
        backgroundColor: '#fff',
      },
      tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#1a5f7a',
        padding: 6,
        borderBottomWidth: 2,
        borderBottomColor: '#d4af37',
      },
      tableHeaderCell: {
        fontSize: 7,
        fontWeight: 'bold',
        color: '#fff',
        flex: 1,
        textAlign: 'center',
      },
      tableRow: {
        flexDirection: 'row',
        padding: 6,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
      },
      tableCell: {
        fontSize: 8,
        flex: 1,
        textAlign: 'center',
        paddingHorizontal: 2,
      },
      tableCellText: {
        color: '#000',
        fontWeight: 'bold',
      },
      totalsSection: {
        marginTop: 15,
        padding: 10,
        backgroundColor: '#f5f5f5',
        borderWidth: 1,
        borderColor: '#d4af37',
      },
      totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 6,
        paddingBottom: 6,
        borderBottomWidth: 1,
        borderBottomColor: '#d4af37',
      },
      totalLabel: {
        fontSize: 9,
        fontWeight: 'bold',
        color: '#1a5f7a',
      },
      totalValue: {
        fontSize: 11,
        fontWeight: 'bold',
        color: '#1a5f7a',
      },
      balanceRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingTop: 6,
        borderTopWidth: 2,
        borderTopColor: '#1a5f7a',
      },
      balanceLabel: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#1a5f7a',
      },
      balanceValue: {
        fontSize: 12,
        fontWeight: 'bold',
        color: balance > 0 ? '#d97706' : '#059669',
      },
      sectionTitle: {
        fontSize: 9,
        fontWeight: 'bold',
        color: '#1a5f7a',
        marginBottom: 8,
        marginTop: 10,
      },
      previousReceiptsTable: {
        marginTop: 10,
        marginBottom: 10,
        borderWidth: 1,
        borderColor: '#d4af37',
        backgroundColor: '#fff',
      },
      previousTableHeader: {
        flexDirection: 'row',
        backgroundColor: '#1a5f7a',
        padding: 5,
        borderBottomWidth: 1,
        borderBottomColor: '#d4af37',
      },
      previousTableHeaderCell: {
        fontSize: 7,
        fontWeight: 'bold',
        color: '#fff',
        flex: 1,
        textAlign: 'center',
      },
      previousTableRow: {
        flexDirection: 'row',
        padding: 5,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
      },
      previousTableCell: {
        fontSize: 7,
        flex: 1,
        textAlign: 'center',
        paddingHorizontal: 2,
      },
      grandTotal: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingTop: 6,
        borderTopWidth: 2,
        borderTopColor: '#1a5f7a',
      },
      footer: {
        marginTop: 20,
        textAlign: 'center',
        fontSize: 7,
        color: '#666',
        lineHeight: 1.4,
      },
      stamp: {
        marginTop: 15,
        textAlign: 'center',
        fontSize: 10,
        fontWeight: 'bold',
        color: '#1a5f7a',
      },
    });

    return (
      <Document>
        <Page style={receiptStyles.page}>
          <View style={receiptStyles.header}>
            <View style={receiptStyles.headerLeft}>
              <Image src={img} style={{ width: 100, height: 100, marginBottom: 5, marginLeft: 100 }} />
            </View>
            <View style={receiptStyles.headerRight}>
              <Text style={receiptStyles.receiptTitle}>RECEIPT</Text>
              <Text style={receiptStyles.companyName}>Diamond Quality Construction L.L.C.SP</Text>
              <Text style={receiptStyles.companyDetails}>TRN: 104836477000001</Text>
              <Text style={receiptStyles.companyDetails}>Sharjah - Muweilah - University City Street</Text>
              <Text style={receiptStyles.companyDetails}>Office No.: 602-58 Floor No.: 6 - Al Areej</Text>
              <Text style={receiptStyles.companyDetails}>0557001522</Text>
              <Text style={receiptStyles.companyDetails}>dqcc01@gmail.com</Text>
              <Text style={receiptStyles.companyDetails}>www.dqcc.ae</Text>
            </View>
          </View>

          <View style={receiptStyles.infoSection}>
            <View style={receiptStyles.infoBlock}>
              <Text style={receiptStyles.infoLabel}>Receipt Number</Text>
              <Text style={receiptStyles.infoValue}>{receipt?.receipt_number || 'N/A'}</Text>
            </View>
            <View style={receiptStyles.infoBlock}>
              <Text style={receiptStyles.infoLabel}>Receipt Date</Text>
              <Text style={receiptStyles.infoValue}>{formatDate(receipt.receipt_date)}</Text>
            </View>
            <View style={receiptStyles.infoBlock}>
              <Text style={receiptStyles.infoLabel}>Invoice Reference</Text>
              <Text style={receiptStyles.infoValue}>{invoiceNo}</Text>
            </View>
          </View>

          {/* Two Column Layout */}
          <View style={receiptStyles.contentContainer}>
            {/* Left Column */}
            <View style={receiptStyles.leftColumn}>
              <View style={receiptStyles.sectionBox}>
                <Text style={receiptStyles.sectionLabel}>From/Customer</Text>
                <Text style={receiptStyles.sectionValue}>{customerName}</Text>
              </View>

              <View style={receiptStyles.sectionBox}>
                <Text style={receiptStyles.sectionLabel}>Customer Currency</Text>
                <Text style={receiptStyles.sectionValue}>AED</Text>
              </View>

              {/* <View style={receiptStyles.sectionBox}>
                <Text style={receiptStyles.sectionLabel}>Bank Account</Text>
                <Text style={receiptStyles.sectionValue}>Bank (AED)</Text>
              </View> */}

              {/* <View style={receiptStyles.sectionBox}>
                <Text style={receiptStyles.sectionLabel}>Statement</Text>
                <Text style={receiptStyles.sectionValue}>-</Text>
              </View>*/}
            </View> 

            {/* Right Column */}
            <View style={receiptStyles.rightColumn}>
              <View style={receiptStyles.row}>
                <Text style={receiptStyles.label}>Receipt No:</Text>
                <Text style={receiptStyles.value}>{receipt.receipt_number}</Text>
              </View>

              <View style={receiptStyles.row}>
                <Text style={receiptStyles.label}>Date Of Receipt</Text>
                <Text style={receiptStyles.value}>{formatDate(receipt.receipt_date)}</Text>
              </View>

              <View style={receiptStyles.row}>
                <Text style={receiptStyles.label}>Reference</Text>
                <Text style={receiptStyles.value}>{invoiceNo}</Text>
              </View>

              {/* <View style={receiptStyles.row}>
                <Text style={receiptStyles.label}>Line:</Text>
                <Text style={receiptStyles.value}>1.00</Text>
              </View> */}

              <View style={receiptStyles.row}>
                <Text style={receiptStyles.label}>Amount</Text>
                <Text style={receiptStyles.value}>{formatCurrency(receipt.amount)}</Text>
              </View>

              <View style={receiptStyles.row}>
                <Text style={receiptStyles.label}>Paid Amount</Text>
                <Text style={receiptStyles.value}>{formatCurrency(receipt.amount)}</Text>
              </View>
            </View>
          </View>

          {/* Allocations Section */}
          <Text style={{ fontSize: 10, fontWeight: 'bold', color: '#1a5f7a', marginBottom: 8 }}>Allocations</Text>

          <View style={receiptStyles.allocationsTable}>
            <View style={receiptStyles.tableHeader}>
              <Text style={receiptStyles.tableHeaderCell}>Type</Text>
              <Text style={receiptStyles.tableHeaderCell}>Number</Text>
              <Text style={receiptStyles.tableHeaderCell}>Date</Text>
              <Text style={receiptStyles.tableHeaderCell}>Total Amount</Text>
              <Text style={receiptStyles.tableHeaderCell}>Last Allocation</Text>
            </View>

            <View style={receiptStyles.tableRow}>
              <Text style={receiptStyles.tableCell}>
                <Text style={receiptStyles.tableCellText}>Invoice</Text>
              </Text>
              <Text style={receiptStyles.tableCell}>
                <Text style={receiptStyles.tableCellText}>{invoiceNo}</Text>
              </Text>
              <Text style={receiptStyles.tableCell}>
                <Text style={receiptStyles.tableCellText}>{formatDate(receipt.receipt_date)}</Text>
              </Text>
              <Text style={receiptStyles.tableCell}>
                <Text style={receiptStyles.tableCellText}>{formatCurrency(invoiceTotal)}</Text>
              </Text>
              <Text style={receiptStyles.tableCell}>
                <Text style={receiptStyles.tableCellText}>{formatCurrency(receipt.amount)}</Text>
              </Text>
            </View>

            <View style={{ ...receiptStyles.tableRow, backgroundColor: '#f9f9f9', borderBottomWidth: 2, borderBottomColor: '#1a5f7a' }}>
              <Text style={{ ...receiptStyles.tableCell, fontWeight: 'bold' }}>
                <Text style={receiptStyles.tableCellText}>Total Allocated</Text>
              </Text>
              <Text style={{ ...receiptStyles.tableCell, flex: 3 }}></Text>
              <Text style={{ ...receiptStyles.tableCell, fontWeight: 'bold' }}>
                <Text style={receiptStyles.tableCellText}>{formatCurrency(receipt.amount)}</Text>
              </Text>
            </View>
          </View>

          {/* Totals Section */}
          <View style={receiptStyles.totalsSection}>
            <View style={receiptStyles.totalRow}>
              <Text style={receiptStyles.totalLabel}>Invoice Total:</Text>
              <Text style={receiptStyles.totalValue}>{formatCurrency(invoiceTotal)}</Text>
            </View>
            <View style={receiptStyles.totalRow}>
              <Text style={receiptStyles.totalLabel}>Total Received:</Text>
              <Text style={receiptStyles.totalValue}>{formatCurrency(totalPaidBefore)}</Text>
            </View>
            <View style={receiptStyles.totalRow}>
              <Text style={receiptStyles.totalLabel}>Payment Mode:</Text>
              <Text style={receiptStyles.totalValue}>{paymentMode}</Text>
            </View>
            <View style={receiptStyles.balanceRow}>
              <Text style={receiptStyles.balanceLabel}>Amount Balance:</Text>
              <Text style={receiptStyles.balanceValue}>{formatCurrency(balance)}</Text>
            </View>
          </View>

          {/* Previous Receipts Section */}
          {allReceiptsForInvoice.length > 0 && (
            <View>
              <Text style={receiptStyles.sectionTitle}>Previous Receipts for this Invoice</Text>
              
              <View style={receiptStyles.previousReceiptsTable}>
                <View style={receiptStyles.previousTableHeader}>
                  <Text style={receiptStyles.previousTableHeaderCell}>Receipt No</Text>
                  <Text style={receiptStyles.previousTableHeaderCell}>Date</Text>
                  <Text style={receiptStyles.previousTableHeaderCell}>Amount</Text>
                  <Text style={receiptStyles.previousTableHeaderCell}>Payment Mode</Text>
                </View>

                {allReceiptsForInvoice.map((prev, idx) => (
                  <View key={idx} style={receiptStyles.previousTableRow}>
                    <Text style={receiptStyles.previousTableCell}>{prev.receipt_number}</Text>
                    <Text style={receiptStyles.previousTableCell}>{formatDate(prev.receipt_date)}</Text>
                    <Text style={receiptStyles.previousTableCell}>{formatCurrency(prev.amount)}</Text>
                    <Text style={receiptStyles.previousTableCell}>{prev.payment_mode}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* Footer */}
          <View style={receiptStyles.footer}>
            <Text>This is a computer-generated receipt and does not require a signature.</Text>
            <Text style={{ marginTop: 6 }}>Generated on {new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })} at {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</Text>
          </View>

          <View style={receiptStyles.stamp}>
            <Text>DQCC</Text>
          </View>
        </Page>
      </Document>
    );
  };

  // Generate and download PDF
  const handleDownloadPDF = async (receipt) => {
    try {
      const doc = <ReceiptPDFDocument receipt={receipt} />;
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Receipt_${receipt.receipt_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF');
    }
  };

  // Handle view receipt
  const handleViewReceipt = (receipt) => {
    setPreviewReceipt(receipt);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <p className="text-lg text-gray-600">Loading receipts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-[#23A19B] rounded-t-2xl p-4 sm:p-8 text-white shadow-xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">Receipts List</h1>
              <p className="text-xs sm:text-base text-white/80">View and manage all payment receipts</p>
            </div>
            <button
              onClick={fetchReceipts}
              className="btn btn-sm sm:btn-md bg-white/20 text-white border-0 hover:bg-white/30 shrink-0"
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-base-100 shadow-xl p-4 sm:p-6 border-t-4 border-primary">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Receipt No.</span>
              </label>
              <input
                type="text"
                placeholder="RCT-001..."
                className="input input-bordered focus:border-primary"
                value={searchReceiptNo}
                onChange={(e) => setSearchReceiptNo(e.target.value)}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Customer Name</span>
              </label>
              <input
                type="text"
                placeholder="Customer name..."
                className="input input-bordered focus:border-primary"
                value={searchCustomer}
                onChange={(e) => setSearchCustomer(e.target.value)}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Invoice No.</span>
              </label>
              <input
                type="text"
                placeholder="INV-00001..."
                className="input input-bordered focus:border-primary"
                value={searchInvoiceNo}
                onChange={(e) => setSearchInvoiceNo(e.target.value)}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Payment Mode</span>
              </label>
              <select
                className="select select-bordered focus:border-primary"
                value={searchPaymentMode}
                onChange={(e) => setSearchPaymentMode(e.target.value)}
              >
                <option value="">All Modes</option>
                <option value="Cash">Cash</option>
                <option value="Bank">Bank</option>
                <option value="Cheque">Cheque</option>
              </select>
            </div>
          </div>

          {(searchReceiptNo || searchCustomer || searchInvoiceNo || searchPaymentMode) && (
            <div className="mt-4">
              <button
                onClick={() => {
                  setSearchReceiptNo('');
                  setSearchCustomer('');
                  setSearchInvoiceNo('');
                  setSearchPaymentMode('');
                }}
                className="btn btn-sm btn-outline"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-error mt-4">
            <span>{error}</span>
            <button onClick={fetchReceipts} className="btn btn-sm">
              Retry
            </button>
          </div>
        )}

        {/* Receipts Table */}
        <div className="bg-base-100 shadow-xl rounded-b-2xl overflow-hidden">
          {filteredReceipts.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-lg text-gray-500">No receipts found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead className="bg-gradient-to-r from-primary to-secondary text-white">
                  <tr>
                    <th>Receipt No.</th>
                    <th>Customer</th>
                    <th>Invoice No.</th>
                    <th>Receipt Date</th>
                    <th>Amount</th>
                    <th>Payment Mode</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReceipts.map((receipt) => (
                    <tr key={receipt.id} className="hover:bg-base-200 transition-colors">
                      <td className="font-bold text-primary">{receipt.receipt_number}</td>
                      <td>
                        <div className="flex flex-col">
                          <span className="font-semibold">{receipt.invoice?.customer?.name || 'Unknown'}</span>
                          <span className="text-xs text-gray-500">{receipt.invoice?.customer?.phone || ''}</span>
                        </div>
                      </td>
                      <td className="font-semibold text-primary">{receipt.invoice?.invoice_no || 'N/A'}</td>
                      <td>{formatDate(receipt.receipt_date)}</td>
                      <td>
                        <span className="font-mono font-bold text-success">
                          {formatCurrency(receipt.amount)}
                        </span>
                      </td>
                      <td>
                        <div className="badge badge-outline">
                          {receipt.payment_mode}
                        </div>
                      </td>
                      <td className="text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleViewReceipt(receipt)}
                            className="btn btn-xs btn-info text-white"
                            title="View Receipt"
                          >
                            👁️ View
                          </button>
                          <button
                            onClick={() => handleDownloadPDF(receipt)}
                            className="btn btn-xs btn-warning text-white"
                            title="Download PDF"
                          >
                            📥 PDF
                          </button>
                          <button
                            onClick={() => handleDelete(receipt.id)}
                            className="btn btn-xs btn-error text-white"
                            title="Delete Receipt"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mt-4 text-center text-sm text-gray-600">
          <p>Showing {filteredReceipts.length} of {receipts.length} receipts</p>
        </div>
      </div>

      {/* Receipt Preview Modal */}
      {previewReceipt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-6 space-y-6 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center border-b pb-4">
              <h2 className="text-2xl font-bold text-gray-900">Receipt Details</h2>
              <button
                onClick={() => setPreviewReceipt(null)}
                className="btn btn-sm btn-circle btn-ghost"
              >
                ✕
              </button>
            </div>

            {/* Receipt Content */}
            <div className="space-y-6">
              {/* Receipt Header */}
              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Receipt Number</p>
                <p className="text-2xl font-bold text-primary">{previewReceipt.receipt_number}</p>
              </div>

              {/* Receipt Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Receipt Date</p>
                  <p className="font-semibold">{formatDate(previewReceipt.receipt_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Payment Mode</p>
                  <p className="font-semibold">{previewReceipt.payment_mode}</p>
                </div>
              </div>

              {/* Amount */}
              <div className="bg-success/10 border-2 border-success rounded-lg p-4">
                <p className="text-sm text-gray-600">Amount Received</p>
                <p className="text-3xl font-bold text-success">
                  {formatCurrency(previewReceipt.amount)}
                </p>
              </div>

              {/* Invoice Information */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-lg mb-3">Related Invoice</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Invoice No.</p>
                    <p className="font-semibold text-primary">{previewReceipt.invoice?.invoice_no || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Invoice Date</p>
                    <p className="font-semibold">{formatDate(previewReceipt.invoice?.invoice_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Invoice Total</p>
                    <p className="font-semibold">
                      {formatCurrency(previewReceipt.ilines_sum_line_total || previewReceipt.invoice?.ilines_sum_line_total || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Invoice Status</p>
                    <p className="font-semibold">{previewReceipt.invoice?.status || 'Unknown'}</p>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-lg mb-3">Customer Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Customer Name</p>
                    <p className="font-semibold">{previewReceipt.invoice?.customer?.name || 'Unknown'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-semibold">{previewReceipt.invoice?.customer?.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Customer Type</p>
                    <p className="font-semibold">{previewReceipt.invoice?.customer?.customer_type || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <p className="font-semibold">{previewReceipt.invoice?.customer?.status || 'Unknown'}</p>
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="border-t pt-4 text-xs text-gray-500">
                <p>Created: {formatDate(previewReceipt.created_at)}</p>
                <p>Last Updated: {formatDate(previewReceipt.updated_at)}</p>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <button
                onClick={() => handleDownloadPDF(previewReceipt)}
                className="btn btn-warning flex-1 text-white"
              >
                📥 Download PDF
              </button>
              <button
                onClick={() => setPreviewReceipt(null)}
                className="btn btn-ghost flex-1"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListReceipts;
